import { useMemo, useState } from 'react';
import { Params, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { useGetCustomerById } from '@utils/api/back-office/customer-bo/use-get-customer-bo';
import { useGetOrgAiSummaryQuota } from '@utils/api/back-office/ai-summary/use-get-org-ai-summary-quota';
import { useGetOrgAiSummaryJobs } from '@utils/api/back-office/ai-summary/use-get-org-ai-summary-jobs';
import { useSetOrgAiSummaryOverride } from '@utils/api/back-office/ai-summary/use-set-org-ai-summary-override';
import Breadcrumb from '@components/atoms/ui-kit/breadcrumb';
import PageHeader from '@components/layout/page-header';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import Loader from '@components/atoms/loaders/loader';
import Button from '@components/atoms/ui-kit/button';
import Input from '@components/atoms/ui-kit/input';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import { PATHS } from '../../../router/paths';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import type { AiSummaryJobRecord } from '@utils/api/back-office/ai-summary/use-get-org-ai-summary-jobs';
import toast from 'react-hot-toast';

export default function CustomerBoAiSummaryPage() {
  const { customerId } = useParams<Params>();
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { data: customerData } = useGetCustomerById(Number(customerId));
  const { data: quotaData, isLoading: quotaLoading } = useGetOrgAiSummaryQuota(
    customerId || ''
  );
  const { data: jobsData, isLoading: jobsLoading } = useGetOrgAiSummaryJobs(
    customerId || ''
  );
  const { mutate: setOverride, isLoading: overrideLoading } =
    useSetOrgAiSummaryOverride();

  const customerName = customerData?.result?.name || t('customer');

  const [overrideInput, setOverrideInput] = useState('');

  const breadcrumbItems = [
    { label: t('customers'), path: PATHS().CUSTOMERS },
    { label: customerName, path: PATHS(customerId || '').CUSTOMER_CONTENT },
    { label: t('bo-customer-ai-summary-title') }
  ];

  const handleSetOverride = () => {
    const val = overrideInput.trim();
    const parsed = val === '' ? null : parseInt(val, 10);
    if (val !== '' && (isNaN(parsed!) || parsed! < 0)) {
      toast.error(
        t('bo-ai-summary-invalid-override') ||
          'Enter a non-negative integer or leave blank to clear.'
      );
      return;
    }
    setOverride(
      { orgId: customerId || '', monthly_limit_override: parsed },
      {
        onSuccess: () => {
          toast.success(t('bo-ai-summary-override-saved') || 'Override saved.');
          setOverrideInput('');
        },
        onError: () => {
          toast.error(t('error') || 'Failed to save override.');
        }
      }
    );
  };

  const columns = useMemo<DataGridColumn<AiSummaryJobRecord>[]>(
    () => [
      {
        id: 'job_id',
        accessorKey: 'job_id',
        header: t('job-id') || 'Job ID',
        minWidth: 280
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('status'),
        maxWidth: 130,
        cell: info => (
          <StatusBadge
            status={info.getValue() as string}
            label={info.getValue() as string}
          />
        )
      },
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: t('created'),
        minWidth: 160,
        cell: info => {
          const v = info.getValue() as string | null;
          return v ? new Date(v).toLocaleString() : '—';
        }
      },
      {
        id: 'date_completed',
        accessorKey: 'date_completed',
        header: t('completed') || 'Completed',
        minWidth: 160,
        cell: info => {
          const v = info.getValue() as string | null;
          return v ? new Date(v).toLocaleString() : '—';
        }
      },
      {
        id: 'output_language',
        accessorKey: 'output_language',
        header: t('language'),
        maxWidth: 100,
        cell: info => (info.getValue() as string | null)?.toUpperCase() ?? '—'
      },
      {
        id: 'model_version',
        accessorKey: 'model_version',
        header: t('model') || 'Model',
        minWidth: 120,
        cell: info => (info.getValue() as string | null) ?? '—'
      }
    ],
    [t]
  );

  const quota = quotaData?.quota;

  return (
    <div className="bg-app flex-1">
      <PageHeader
        title={t('bo-customer-ai-summary-title') || 'AI Summary Audit'}
        caption={customerName}
      />

      <div className="px-4 py-4 flex flex-col gap-6">
        <Breadcrumb items={breadcrumbItems} />

        {/* Quota overview */}
        {quotaLoading ? (
          <Loader />
        ) : quota ? (
          <div
            className="rounded-2xl border p-4 flex flex-col gap-3"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('firstLayer')
            }}
          >
            <h2
              className="text-sm font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {t('bo-ai-summary-quota-title') || 'Current Billing Period'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                {
                  label: t('bo-ai-summary-enabled') || 'Enabled',
                  value: quota.enabled ? t('yes') || 'Yes' : t('no') || 'No'
                },
                {
                  label: t('bo-ai-summary-limit') || 'Limit',
                  value:
                    quota.limit != null
                      ? String(quota.limit)
                      : t('unlimited') || 'Unlimited'
                },
                {
                  label: t('bo-ai-summary-used') || 'Used',
                  value: String(quota.used)
                },
                {
                  label: t('bo-ai-summary-reserved') || 'Reserved',
                  value: String(quota.reserved)
                },
                {
                  label: t('bo-ai-summary-remaining') || 'Remaining',
                  value:
                    quota.remaining != null
                      ? String(quota.remaining)
                      : t('unlimited') || 'Unlimited'
                }
              ].map(item => (
                <div key={item.label} className="flex flex-col gap-1">
                  <span
                    className="text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {item.label}
                  </span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
            {quota.reset_at && (
              <p
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('bo-ai-summary-resets') || 'Resets'}{' '}
                {new Date(quota.reset_at).toLocaleDateString()}
              </p>
            )}
          </div>
        ) : null}

        {/* Per-org override */}
        <div
          className="rounded-2xl border p-4 flex flex-col gap-3"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('firstLayer')
          }}
        >
          <h2
            className="text-sm font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('bo-ai-summary-override-title') || 'Monthly Limit Override'}
          </h2>
          <p className="text-xs" style={{ color: getColour('inactiveText') }}>
            {t('bo-ai-summary-override-description') ||
              'Override the plan limit for this organization. Enter 0 to disable AI summaries. Leave blank and save to remove the override.'}
          </p>
          {quotaData && (
            <p className="text-xs" style={{ color: getColour('inactiveText') }}>
              {t('bo-ai-summary-current-override') || 'Current override'}:{' '}
              <strong style={{ color: getColour('normalText') }}>
                {quotaData.monthly_limit_override != null
                  ? String(quotaData.monthly_limit_override)
                  : t('none') || 'None (using plan limit)'}
              </strong>
            </p>
          )}
          <div className="flex items-end gap-2 max-w-xs">
            <Input
              label={
                t('bo-ai-summary-override-label') || 'New limit (blank = clear)'
              }
              placeholder={t('bo-ai-summary-override-placeholder') || 'e.g. 50'}
              value={overrideInput}
              onChange={e => setOverrideInput(e.target.value)}
              type="number"
            />
            <Button
              label={t('save') || 'Save'}
              size="small"
              variant="primary"
              onClick={handleSetOverride}
              loading={overrideLoading}
              className="min-w-fit"
              disabled={overrideLoading}
              icon={<RefreshCw className="h-3.5 w-3.5" />}
            />
          </div>
        </div>

        {/* Job-level audit */}
        <div className="flex flex-col gap-2">
          <h2
            className="text-sm font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('bo-ai-summary-jobs-title') || 'Recent Jobs'}
          </h2>
          {jobsLoading ? (
            <Loader />
          ) : (
            <DataGridV2
              data={(jobsData?.result ?? []).map(j => ({ ...j, id: j.job_id }))}
              columns={columns}
              totalItems={jobsData?.total_results ?? 0}
              enablePagination
            />
          )}
        </div>
      </div>
    </div>
  );
}
